export const resolveApiBase = (nodeEnv, configuredBase) =>
  nodeEnv === "production" ? "" : String(configuredBase || "").trim().replace(/\/$/, "");

// Production uses Vercel's same-origin /api and /uploads rewrites. This keeps
// browsers independent from the intermittently failing public API DNS record.
export const API_BASE = resolveApiBase(process.env.NODE_ENV, process.env.REACT_APP_API_BASE_URL);

export const apiUrl = (path) => {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${normalizedPath}`;
};

const legacyUploadHosts = new Set([
  ["maielmokadem-production", "up", "railway", "app"].join("."),
]);
const proxiedUploadHosts = new Set(["api.maielmokadem.com"]);

export const isUnavailableLegacyUpload = (value) => {
  if (typeof value !== "string" || !value.startsWith("http")) return false;

  try {
    const url = new URL(value);
    return legacyUploadHosts.has(url.hostname) && url.pathname.startsWith("/uploads/");
  } catch {
    return false;
  }
};

export const sanitizeLegacyMedia = (value) => {
  if (Array.isArray(value)) return value.map(sanitizeLegacyMedia);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, sanitizeLegacyMedia(item)])
    );
  }
  if (isUnavailableLegacyUpload(value)) return "";
  if (typeof value === "string" && !API_BASE && value.startsWith("http")) {
    try {
      const url = new URL(value);
      if (proxiedUploadHosts.has(url.hostname) && url.pathname.startsWith("/uploads/")) {
        return `${url.pathname}${url.search}`;
      }
    } catch {
      return value;
    }
  }
  return value;
};

export const fetchWithTimeout = (url, options = {}, timeoutMs = 12000) => {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  const method = String(options.method || "GET").toUpperCase();
  const requestOptions = method === "GET" ? { cache: "no-store", ...options } : options;
  return fetch(url, { ...requestOptions, signal: controller.signal }).finally(() => {
    globalThis.clearTimeout(timeout);
  });
};

const fetchJson = async (path, options = {}) => {
  const response = await fetchWithTimeout(apiUrl(path), options);
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(data?.error || data?.detail || "API request failed");
    error.status = response.status;
    throw error;
  }

  return sanitizeLegacyMedia(data);
};

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const jsonHeaders = (token) => ({
  "Content-Type": "application/json",
  ...authHeaders(token),
});

// ── Public reads ──────────────────────────────────────────────────────────────

export const getArticles = async () => {
  const response = await fetchJson("/api/articles");
  return response?.data || [];
};

export const getArticleBySlug = async (slug) => {
  const response = await fetchJson(`/api/articles/${encodeURIComponent(slug)}`);
  return response?.data || null;
};

export const getProfile = async () => {
  const response = await fetchJson("/api/profile");
  return response?.data || null;
};

export const getFeatured = async () => {
  const response = await fetchJson("/api/featured");
  return response?.data || null;
};

export const getHighlights = async () => {
  const response = await fetchJson("/api/highlights");
  return response?.data || [];
};

export const getPullQuotes = async () => {
  const response = await fetchJson("/api/pull-quotes");
  return response?.data || [];
};

export const getPublications = async () => {
  const response = await fetchJson("/api/publications");
  return response?.data || [];
};

export const getCareer = async () => {
  const response = await fetchJson("/api/career");
  return response?.data || [];
};

export const getSiteSections = async () => {
  const response = await fetchJson("/api/site-sections");
  return response?.data || [];
};

export const getSiteSectionsByPage = async (page) => {
  const response = await fetchJson(`/api/site-sections/${encodeURIComponent(page)}`);
  return response?.data || [];
};

export const searchArticles = async (q) => {
  const response = await fetchJson(`/api/search?q=${encodeURIComponent(q)}`);
  return response?.data || [];
};

export const postNewsletter = async (email) => {
  const response = await fetchJson("/api/newsletter", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return response?.data || null;
};

// ── Admin: Inquiries ──────────────────────────────────────────────────────────

export const getInquiries = async (token) => {
  const response = await fetchJson("/api/inquiries", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

// ── Admin: Password reset ─────────────────────────────────────────────────────

export const forgotAdminPassword = async (email) => {
  return fetchJson("/api/auth/forgot-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
};

export const resetAdminPassword = async (token, password) => {
  return fetchJson("/api/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
};

// ── Admin: Articles ───────────────────────────────────────────────────────────

export const adminGetArticles = async (token) => {
  const response = await fetchJson("/api/articles", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminCreateArticle = async (token, article) => {
  const response = await fetchJson("/api/articles", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(article),
  });
  return response?.data || null;
};

export const adminUpdateArticle = async (token, id, article) => {
  const response = await fetchJson(`/api/articles/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(article),
  });
  return response?.data || null;
};

export const adminDeleteArticle = async (token, id) => {
  const response = await fetchJson(`/api/articles/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return response?.data || null;
};

// ── Admin: Profile ────────────────────────────────────────────────────────────

export const adminGetProfile = async (token) => {
  const response = await fetchJson("/api/profile", {
    headers: authHeaders(token),
  });
  return response?.data || null;
};

export const adminUpdateProfile = async (token, profile) => {
  const response = await fetchJson("/api/profile", {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(profile),
  });
  return response?.data || null;
};

// ── Admin: Featured ───────────────────────────────────────────────────────────

export const adminGetFeatured = async (token) => {
  const response = await fetchJson("/api/featured", {
    headers: authHeaders(token),
  });
  return response?.data || null;
};

export const adminUpdateFeatured = async (token, data) => {
  const response = await fetchJson("/api/featured", {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

// ── Admin: Highlights ─────────────────────────────────────────────────────────

export const adminGetHighlights = async (token) => {
  const response = await fetchJson("/api/highlights", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminCreateHighlight = async (token, data) => {
  const response = await fetchJson("/api/highlights", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminUpdateHighlight = async (token, id, data) => {
  const response = await fetchJson(`/api/highlights/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminDeleteHighlight = async (token, id) => {
  const response = await fetchJson(`/api/highlights/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return response?.data || null;
};

// ── Admin: Pull Quotes ────────────────────────────────────────────────────────

export const adminGetPullQuotes = async (token) => {
  const response = await fetchJson("/api/pull-quotes", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminCreatePullQuote = async (token, data) => {
  const response = await fetchJson("/api/pull-quotes", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminUpdatePullQuote = async (token, id, data) => {
  const response = await fetchJson(`/api/pull-quotes/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminDeletePullQuote = async (token, id) => {
  const response = await fetchJson(`/api/pull-quotes/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return response?.data || null;
};

// ── Admin: Publications ───────────────────────────────────────────────────────

export const adminGetPublications = async (token) => {
  const response = await fetchJson("/api/publications", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminCreatePublication = async (token, data) => {
  const response = await fetchJson("/api/publications", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminUpdatePublication = async (token, id, data) => {
  const response = await fetchJson(`/api/publications/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminDeletePublication = async (token, id) => {
  const response = await fetchJson(`/api/publications/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return response?.data || null;
};

// ── Admin: Career ─────────────────────────────────────────────────────────────

export const adminGetCareer = async (token) => {
  const response = await fetchJson("/api/career", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminCreateCareer = async (token, data) => {
  const response = await fetchJson("/api/career", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminUpdateCareer = async (token, id, data) => {
  const response = await fetchJson(`/api/career/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminDeleteCareer = async (token, id) => {
  const response = await fetchJson(`/api/career/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return response?.data || null;
};

// ── Admin: Stats ──────────────────────────────────────────────────────────────

export const adminGetSiteSections = async (token) => {
  const response = await fetchJson("/api/site-sections", {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminGetSiteSectionsByPage = async (token, page) => {
  const response = await fetchJson(`/api/site-sections/${encodeURIComponent(page)}`, {
    headers: authHeaders(token),
  });
  return response?.data || [];
};

export const adminCreateSiteSection = async (token, data) => {
  const response = await fetchJson("/api/site-sections", {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(data),
  });
  return response?.data || null;
};

export const adminUpdateSiteSection = async (token, page, sectionKey, data) => {
  const response = await fetchJson(
    `/api/site-sections/${encodeURIComponent(page)}/${encodeURIComponent(sectionKey)}`,
    {
      method: "PUT",
      headers: jsonHeaders(token),
      body: JSON.stringify(data),
    }
  );
  return response?.data || null;
};

export const adminDeleteSiteSection = async (token, page, sectionKey) => {
  const response = await fetchJson(
    `/api/site-sections/${encodeURIComponent(page)}/${encodeURIComponent(sectionKey)}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
    }
  );
  return response?.data || null;
};

export const getAdminStats = async (token) => {
  const response = await fetchJson("/api/admin/stats", {
    headers: authHeaders(token),
  });
  return response?.data || null;
};

// ── Admin: Uploads ────────────────────────────────────────────────────────────

export const adminUploadImage = async (token, file) => {
  const form = new FormData();
  form.append("file", file);
  const response = await fetchJson("/api/uploads", {
    method: "POST",
    headers: authHeaders(token),
    body: form,
  });
  return response?.data || null;
};
